import React, { useState } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Plus, X, Upload, Image } from "lucide-react";
import { SecondMeConfig } from '@/components/second-me/types';
import { showcaseService } from '@/services/showcase/showcaseService';

interface ShareAgentModalProps {
  config: SecondMeConfig;
  trigger?: React.ReactNode;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

const formSchema = z.object({
  title: z.string().min(3, { message: "Title must be at least 3 characters" }),
  description: z.string().min(10, { message: "Description must be at least 10 characters" }),
  thumbnailUrl: z.string().url().optional(),
  tags: z.array(z.string()),
  isPublic: z.boolean(),
  sharedWith: z.array(z.string().uuid()).optional(),
  complexity: z.enum(["beginner", "intermediate", "advanced"]),
  useCases: z.array(z.string()),
  termsAccepted: z.boolean().refine(val => val === true, {
    message: "You must accept the terms to share your agent"
  })
});

type FormValues = z.infer<typeof formSchema>;

/**
 * Modal component for publishing agents to the showcase
 */
export const ShareAgentModal: React.FC<ShareAgentModalProps> = ({
  config,
  trigger,
  onSuccess,
  onError,
}) => {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [useCases, setUseCases] = useState<string[]>([]);
  const [useCaseInput, setUseCaseInput] = useState('');
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);

  // Initialize form with default values
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: config.name,
      description: config.description || '',
      thumbnailUrl: '',
      tags: [],
      isPublic: false,
      complexity: 'intermediate',
      useCases: [],
      termsAccepted: false
    }
  });

  // Watch form values
  const isPublic = form.watch('isPublic');

  // Update form values when tags change
  React.useEffect(() => {
    form.setValue('tags', tags);
  }, [tags, form]);

  // Update form values when use cases change
  React.useEffect(() => {
    form.setValue('useCases', useCases);
  }, [useCases, form]);

  // Handle tag input
  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setTags(tags.filter(t => t !== tag));
  };

  // Handle use case input
  const handleAddUseCase = () => {
    if (useCaseInput.trim() && !useCases.includes(useCaseInput.trim())) {
      setUseCases([...useCases, useCaseInput.trim()]);
      setUseCaseInput('');
    }
  };

  const handleRemoveUseCase = (useCase: string) => {
    setUseCases(useCases.filter(uc => uc !== useCase));
  };

  // Handle thumbnail upload
  const handleThumbnailUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // In a real implementation, you would upload the file to your storage service
      // For now, we'll just use a local URL for preview
      const previewUrl = URL.createObjectURL(file);
      setThumbnailPreview(previewUrl);
      form.setValue('thumbnailUrl', previewUrl);
    }
  };

  // Handle form submission
  const onSubmit = async (values: FormValues) => {
    setIsSubmitting(true);
    try {
      // In a real implementation, you would handle the thumbnail upload
      // and get a permanent URL before saving
      
      // Publish the agent to showcase
      await showcaseService.publishAgentToShowcase(config, {
        title: values.title,
        description: values.description,
        thumbnailUrl: values.thumbnailUrl,
        tags: values.tags,
        isPublic: values.isPublic,
        sharedWith: values.sharedWith,
        metadata: {
          complexity: values.complexity,
          useCases: values.useCases,
          creatorName: config.metadata?.creatorName || 'Anonymous',
        }
      });
      
      setOpen(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error publishing agent:', error);
      if (onError) onError(error as Error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || <Button>Share Agent</Button>}
      </DialogTrigger>
      
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Share Your Agent</DialogTitle>
          <DialogDescription>
            Publish your Second Me agent to the showcase gallery. This will make your agent
            available to others based on your visibility settings.
          </DialogDescription>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Title Field */}
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title</FormLabel>
                  <FormControl>
                    <Input placeholder="My Amazing Agent" {...field} />
                  </FormControl>
                  <FormDescription>
                    Enter a catchy title for your agent (3-50 characters).
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            {/* Description Field */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Describe what your agent does and what makes it special..." 
                      {...field} 
                      rows={3}
                    />
                  </FormControl>
                  <FormDescription>
                    Enter a detailed description (10-500 characters).
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            {/* Thumbnail Upload */}
            <FormField
              control={form.control}
              name="thumbnailUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Thumbnail</FormLabel>
                  <div className="flex items-center gap-4">
                    <Avatar className="h-16 w-16 border">
                      <AvatarImage src={thumbnailPreview || field.value || '/placeholder.svg'} />
                      <AvatarFallback>
                        {config.name.substring(0, 2).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    
                    <div className="flex-grow">
                      <FormControl>
                        <div className="flex items-center">
                          <Input 
                            type="file" 
                            accept="image/*"
                            className="hidden"
                            id="thumbnail-upload"
                            onChange={handleThumbnailUpload}
                          />
                          <label 
                            htmlFor="thumbnail-upload"
                            className="inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input hover:bg-accent hover:text-accent-foreground h-10 px-4 py-2 cursor-pointer"
                          >
                            <Upload className="mr-2 h-4 w-4" />
                            Upload
                          </label>
                        </div>
                      </FormControl>
                      <FormDescription>
                        Upload a square image (recommended: 256x256px).
                      </FormDescription>
                    </div>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            {/* Tags Field */}
            <FormItem>
              <FormLabel>Tags</FormLabel>
              <div className="flex flex-wrap gap-2 mb-2">
                {tags.map(tag => (
                  <Badge key={tag} variant="secondary" className="gap-1">
                    {tag}
                    <button 
                      type="button" 
                      onClick={() => handleRemoveTag(tag)}
                      className="ml-1 rounded-full hover:bg-muted/80 w-4 h-4 inline-flex items-center justify-center"
                    >
                      <X className="h-3 w-3" />
                      <span className="sr-only">Remove {tag}</span>
                    </button>
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Add a tag"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  size="icon"
                  onClick={handleAddTag}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <FormDescription>
                Add tags to help others find your agent (e.g., "education", "productivity").
              </FormDescription>
            </FormItem>
            
            {/* Complexity Field */}
            <FormField
              control={form.control}
              name="complexity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Complexity</FormLabel>
                  <FormControl>
                    <Select 
                      value={field.value} 
                      onValueChange={field.onChange}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select complexity" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="beginner">Beginner</SelectItem>
                        <SelectItem value="intermediate">Intermediate</SelectItem>
                        <SelectItem value="advanced">Advanced</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormDescription>
                    Indicate the complexity level of your agent.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            {/* Use Cases Field */}
            <FormItem>
              <FormLabel>Use Cases</FormLabel>
              <div className="flex flex-wrap gap-2 mb-2">
                {useCases.map(useCase => (
                  <Badge key={useCase} variant="outline" className="gap-1">
                    {useCase}
                    <button 
                      type="button" 
                      onClick={() => handleRemoveUseCase(useCase)}
                      className="ml-1 rounded-full hover:bg-muted/80 w-4 h-4 inline-flex items-center justify-center"
                    >
                      <X className="h-3 w-3" />
                      <span className="sr-only">Remove {useCase}</span>
                    </button>
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  value={useCaseInput}
                  onChange={(e) => setUseCaseInput(e.target.value)}
                  placeholder="Add a use case"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddUseCase();
                    }
                  }}
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  size="icon"
                  onClick={handleAddUseCase}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <FormDescription>
                Describe scenarios where your agent is useful.
              </FormDescription>
            </FormItem>
            
            {/* Visibility Settings */}
            <FormField
              control={form.control}
              name="isPublic"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Public Visibility</FormLabel>
                    <FormDescription>
                      Make your agent visible to everyone in the showcase.
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            {/* Terms Acceptance */}
            <FormField
              control={form.control}
              name="termsAccepted"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>
                      I agree to the Terms of Service
                    </FormLabel>
                    <FormDescription>
                      By publishing this agent, you grant others the right to view, fork, and use your agent according to our terms.
                    </FormDescription>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Publishing...' : 'Publish Agent'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};